import { APIService } from "../../../common/API";
import { FormatMoney } from "../../../common/helpers";
import { parsedUser } from "../../../common/GetCurrentUser";
import { useEffect, useState } from "react";
import { Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import type { ChartOptions } from "chart.js";
import SalesByMonthChart from "./SalesByMonthChart";
import placeEnhancementsService, { PlaceAccountLink } from "@/services/PlaceEnhancementsService";

const Summary = (props) => {
    const { t } = useTranslation("global");
    const user = parsedUser();
    const [record, setRecord] = useState<any>(null);
    const [chartData, setChartData] = useState<any>(null);
    const [amountChartData, setAmountChartData] = useState<any>(null);

    // Multiple Linked Customer Accounts state
    const [accountLinks, setAccountLinks] = useState<PlaceAccountLink[]>([]);
    const [showAccountModal, setShowAccountModal] = useState(false);
    const [editingAccountId, setEditingAccountId] = useState<string | null>(null);
    const [isSavingAccount, setIsSavingAccount] = useState(false);

    // Account link form states
    const [clientCodeInput, setClientCodeInput] = useState("");
    const [clientNameInput, setClientNameInput] = useState("");

    // ERP Autocomplete suggestions state
    const [suggestions, setSuggestions] = useState<Array<{ code: string; name: string }>>([]);
    const [isSearchingErp, setIsSearchingErp] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);

    ChartJS.register(
        CategoryScale,
        LinearScale,
        BarElement,
        Title,
        Tooltip,
        Legend
    );

    const chartOptions: ChartOptions<"bar"> = {
        responsive: true,
        plugins: {
            legend: { position: "top" },
            title: {
                display: true,
                text: t("Invoices and Quotes per Month"),
            },
        },
    };
    const chartAmountOptions: ChartOptions<"bar"> = {
        responsive: true,
        plugins: {
            legend: { position: "top" },
            title: {
                display: true,
                text: t("Invoices and Quotes Amount per Month"),
            },
        },
    };

    const fetchErpSuggestions = async (term: string) => {
        if (!term || term.trim().length < 2) {
            setSuggestions([]);
            setShowSuggestions(false);
            return;
        }
        try {
            setIsSearchingErp(true);
            const orgId = user?.OrganizationId || "ochoa";
            const results: Array<{ code: string; name: string }> = [];

            // 1. Try place/SearchByCode (already deployed on production api.bi.shearly.com)
            try {
                const placeRes = await APIService.getAction(`place/SearchByCode?code=${encodeURIComponent(term)}&organizationId=${orgId}`);
                if (placeRes?.status === 200 && placeRes?.data?.response) {
                    const p = placeRes.data.response;
                    const code = p.creditCode || p.CreditCode || term;
                    const name = p.name || p.Name || "";
                    if (name) {
                        results.push({ code, name });
                    }
                }
            } catch (e) {
                // Ignore search error
            }

            // 2. Try Customer/List (already deployed on production api.bi.shearly.com)
            try {
                const custRes = await APIService.getAction(`Customer/List?OrganizationId=${orgId}&Search=${encodeURIComponent(term)}`);
                if (custRes?.status === 200 && Array.isArray(custRes?.data?.response)) {
                    custRes.data.response.forEach((c: any) => {
                        const code = c.customerCode || c.CustomerCode || c.code || "";
                        const name = c.customerName || c.CustomerName || c.name || "";
                        if (code || name) {
                            if (!results.some(r => r.code === code)) {
                                results.push({ code, name });
                            }
                        }
                    });
                }
            } catch (e) {
                // Ignore search error
            }

            // 3. Fallback to customer/SearchErpClients if present
            if (results.length === 0) {
                try {
                    const res = await APIService.getAction(`customer/SearchErpClients?OrganizationId=${orgId}&Search=${encodeURIComponent(term)}`);
                    if (res?.status === 200 && Array.isArray(res?.data?.response)) {
                        res.data.response.forEach((item: any) => {
                            if (!results.some(r => r.code === item.code)) {
                                results.push({ code: item.code, name: item.name });
                            }
                        });
                    }
                } catch (e) {
                    // Endpoint not deployed on production yet
                }
            }

            setSuggestions(results);
            setShowSuggestions(results.length > 0);
        } catch (e) {
            setSuggestions([]);
            setShowSuggestions(false);
        } finally {
            setIsSearchingErp(false);
        }
    };

    const reloadAccountLinks = (backendClientCode?: string, backendClientName?: string) => {
        if (!props?.model?.id) return;
        let links = placeEnhancementsService.getAccountLinks(props.model.id);

        // If local list is empty but backend/place model has a primary code, seed initial entry
        const code = backendClientCode || props.model?.creditCode || props.model?.customerId || "";
        const name = backendClientName || props.model?.customer?.name || props.model?.name || "";

        if (links.length === 0 && code) {
            placeEnhancementsService.addAccountLink(props.model.id, {
                accountNumber: code,
                customerName: name,
            });
            links = placeEnhancementsService.getAccountLinks(props.model.id);
        }

        setAccountLinks(links);
    };

    const buildMonthlyChart = (records) => {
        const labels = records.map(item => item.monthLabel || item.MonthLabel || "");
        const invoiceCounts = records.map(item => item.invoices ?? item.Invoices ?? 0);
        const quoteCounts = records.map(item => item.quotes ?? item.Quotes ?? 0);

        setChartData({
            labels,
            datasets: [
                {
                    label: t("Invoices"),
                    data: invoiceCounts,
                    backgroundColor: "rgba(59, 130, 246, 0.6)",
                },
                {
                    label: t("Quotes"),
                    data: quoteCounts,
                    backgroundColor: "rgba(234, 88, 12, 0.6)",
                },
            ],
        });
    };

    const buildAmountMonthlyChart = (records) => {
        const labels = records.map(item => item.monthLabel || item.MonthLabel || "");
        const invoiceAmounts = records.map(item => item.invoices ?? item.Invoices ?? 0);
        const quoteAmounts = records.map(item => item.quotes ?? item.Quotes ?? 0);

        setAmountChartData({
            labels,
            datasets: [
                {
                    label: t("Invoices"),
                    data: invoiceAmounts,
                    backgroundColor: "rgba(59, 130, 246, 0.6)",
                },
                {
                    label: t("Quotes"),
                    data: quoteAmounts,
                    backgroundColor: "rgba(234, 88, 12, 0.6)",
                },
            ],
        });
    };

    const bindData = async () => {
        try {
            const [summaryRequest, monthlyRequest, amountMonthlyRequest] = await Promise.all([
                APIService.getAction(`placeDashboard/summary?id=${props.model.id}`),
                APIService.getAction(`placeDashboard/InvoicesQuotesMonthly?id=${props.model.id}`),
                APIService.getAction(`placeDashboard/AmountInvoicesQuotesMonthly?id=${props.model.id}`),
            ]);

            let bCode = "";
            let bName = "";

            if (summaryRequest.status === 200) {
                setRecord(summaryRequest.data.response);
                bCode = summaryRequest.data.response?.clientCode || "";
                bName = summaryRequest.data.response?.clientName || "";
            }

            reloadAccountLinks(bCode, bName);

            const monthlyRecords = monthlyRequest.status === 200 ? monthlyRequest.data.response || [] : [];
            const amountMonthlyRecords = amountMonthlyRequest.status === 200 ? amountMonthlyRequest.data.response || [] : [];
            buildMonthlyChart(monthlyRecords);
            buildAmountMonthlyChart(amountMonthlyRecords);
        } catch (e) {
            reloadAccountLinks();
        }
    };

    useEffect(() => {
        if (props?.model?.id) {
            bindData();
        }
    }, [props?.model?.id]);

    const handleOpenAddAccount = () => {
        setEditingAccountId(null);
        setClientCodeInput("");
        setClientNameInput("");
        setSuggestions([]);
        setShowSuggestions(false);
        setShowAccountModal(true);
    };

    const handleOpenEditAccount = (acc: PlaceAccountLink) => {
        setEditingAccountId(acc.id);
        setClientCodeInput(acc.accountNumber || "");
        setClientNameInput(acc.customerName || "");
        setSuggestions([]);
        setShowSuggestions(false);
        setShowAccountModal(true);
    };

    const handleDeleteAccount = (id: string) => {
        if (window.confirm(t("¿Seguro que deseas eliminar esta cuenta vinculada?"))) {
            placeEnhancementsService.deleteAccountLink(id);
            reloadAccountLinks();
        }
    };

    const handleSaveAccountLink = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!clientCodeInput.trim() || !clientNameInput.trim()) return;

        try {
            setIsSavingAccount(true);

            if (editingAccountId) {
                placeEnhancementsService.updateAccountLink(editingAccountId, {
                    accountNumber: clientCodeInput,
                    customerName: clientNameInput,
                });
            } else {
                placeEnhancementsService.addAccountLink(props.model.id, {
                    accountNumber: clientCodeInput,
                    customerName: clientNameInput,
                });
            }

            // Sync main place credit code to backend
            const updatedModel = {
                ...props.model,
                creditCode: clientCodeInput,
            };
            await APIService.postAction("place/AddEdit", updatedModel);

            setShowAccountModal(false);
            await bindData();
        } catch (ex) {
            console.error("Error saving account link:", ex);
        } finally {
            setIsSavingAccount(false);
        }
    };

    return <div className="mb-4">
        <Row>
            {/* Multiple Linked ERP Accounts Card */}
            <Col xs={12} className="mb-3">
                <Card className="border shadow-sm rounded-3">
                    <Card.Header className="bg-light py-2 d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center gap-2">
                            <span className="fw-bold small text-uppercase text-muted">
                                <i className="fas fa-building-user me-2 text-primary" />
                                {t("Cuentas Vinculadas ERP")}
                            </span>
                            <Badge bg="primary" className="px-2 py-1 fw-semibold rounded-pill">
                                {accountLinks.length > 0
                                    ? `${accountLinks.length} ${accountLinks.length === 1 ? 'cuenta' : 'cuentas'}`
                                    : '0 cuentas'}
                            </Badge>
                        </div>
                        <Button
                            variant="primary"
                            size="sm"
                            className="py-1 px-3 fw-semibold rounded-2 shadow-sm"
                            onClick={handleOpenAddAccount}
                        >
                            <i className="fas fa-plus me-1" />
                            {t("Agregar Cuenta ERP")}
                        </Button>
                    </Card.Header>
                    <Card.Body className="p-0">
                        {accountLinks.length === 0 ? (
                            <div className="text-center py-4 text-muted small">
                                <i className="fas fa-link-slash fa-2x mb-2 opacity-50" />
                                <div>{t("No hay cuentas ERP vinculadas a este lugar.")}</div>
                                <Button variant="outline-primary" size="sm" className="mt-2 rounded-2" onClick={handleOpenAddAccount}>
                                    <i className="fas fa-plus me-1" />
                                    {t("Vincular Primera Cuenta")}
                                </Button>
                            </div>
                        ) : (
                            <Table hover responsive className="align-middle small mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th>{t("CÓDIGO DEL CLIENTE / ERP")}</th>
                                        <th>{t("NOMBRE DEL CLIENTE / RAZÓN SOCIAL")}</th>
                                        <th className="text-end pe-3">{t("ACCIONES")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {accountLinks.map((acc, index) => (
                                        <tr key={acc.id || index}>
                                            <td className="fw-bold font-monospace text-primary">
                                                <i className="fas fa-id-card me-2 opacity-75" />
                                                {acc.accountNumber || "Sin código"}
                                            </td>
                                            <td className="fw-semibold text-dark">
                                                {acc.customerName || "Sin nombre registrado"}
                                            </td>
                                            <td className="text-end pe-3">
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    className="me-1 px-2 py-0"
                                                    title={t("Editar")}
                                                    onClick={() => handleOpenEditAccount(acc)}
                                                >
                                                    <i className="fas fa-pen" />
                                                </Button>
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    className="px-2 py-0"
                                                    title={t("Eliminar")}
                                                    onClick={() => handleDeleteAccount(acc.id)}
                                                >
                                                    <i className="fas fa-trash-can" />
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        )}
                    </Card.Body>
                </Card>
            </Col>

            {record?.ochoaInfo && <>
                <Col xs={6} className="mb-2">
                    <Card className="shadow-sm">
                        <Card.Body className="py-2">
                            <h3>RD$ {FormatMoney(record?.totalInvoiced || 0)}</h3>
                        </Card.Body>
                        <Card.Footer className="py-2">
                            {t("Total Invoices")}
                        </Card.Footer>
                    </Card>
                </Col>
                <Col xs={6} className="mb-2">
                    <Card className="shadow-sm">
                        <Card.Body className="py-2">
                            <h3>RD$ {FormatMoney(record?.totalQuoted || 0)}</h3>
                        </Card.Body>
                        <Card.Footer className="py-2">
                            {t("Total Quotations")}
                        </Card.Footer>
                    </Card>
                </Col>
                <Col xs={6} md={4} className="mb-2">
                    <Card className="shadow-sm">
                        <Card.Body className="py-2">
                            <h3>{record?.amountOfQuotes || 0}</h3>
                        </Card.Body>
                        <Card.Footer className="py-2">
                            {t("Amount of Quotes")}
                        </Card.Footer>
                    </Card>
                </Col>
                <Col xs={6} md={4} className="mb-2">
                    <Card className="shadow-sm">
                        <Card.Body className="py-2">
                            <h3>{record?.amountOfInvoices || 0}</h3>
                        </Card.Body>
                        <Card.Footer className="py-2">
                            {t("Amount of Invoices")}
                        </Card.Footer>
                    </Card>
                </Col>
                <Col xs={6} md={4} className="mb-2">
                    <Card className="shadow-sm">
                        <Card.Body className="py-2">
                            <h3>{record?.totalQuotedNotInvoiced || 0}</h3>
                        </Card.Body>
                        <Card.Footer className="py-2">
                            {t("Quotes without invoices")}
                        </Card.Footer>
                    </Card>
                </Col>
                {amountChartData && (
                    <Col xs={12} className="mb-3">
                        <Card className="shadow-sm">
                            <Card.Body>
                                <Bar options={chartAmountOptions} data={amountChartData} />
                            </Card.Body>
                        </Card>
                    </Col>
                )}
                {chartData && (
                    <Col xs={12} className="mb-3">
                        <Card className="shadow-sm">
                            <Card.Body>
                                <Bar options={chartOptions} data={chartData} />
                            </Card.Body>
                        </Card>
                    </Col>
                )}

                {/* Sales by Month Chart */}
                <Col xs={12} className="mb-3">
                    <SalesByMonthChart placeId={props.model.id} />
                </Col>
            </>}

            <Col xs={6} md={4} className="mb-2">
                <Card className="shadow-sm">
                    <Card.Body className="py-2">
                        <h3>{record?.totalVisits || 0}</h3>
                    </Card.Body>
                    <Card.Footer className="py-2">
                        {t("Total Visits")}
                    </Card.Footer>
                </Card>
            </Col>
            <Col xs={6} md={4} className="mb-2">
                <Card className="shadow-sm">
                    <Card.Body className="py-2">
                        <h3>{record?.totalDispatchVisits || 0}</h3>
                    </Card.Body>
                    <Card.Footer className="py-2">
                        {t("Total Dispatched Visits")}
                    </Card.Footer>
                </Card>
            </Col>
        </Row>

        {/* Modal for adding/editing ERP Customer Account with Autocomplete */}
        <Modal show={showAccountModal} onHide={() => setShowAccountModal(false)} centered>
            <Form onSubmit={handleSaveAccountLink}>
                <Modal.Header closeButton>
                    <Modal.Title className="fs-6 fw-bold">
                        {editingAccountId ? t("Editar Cuenta de Cliente ERP") : t("Vincular Nueva Cuenta de Cliente ERP")}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="small">
                    <Row className="g-3">
                        <Col xs={12} className="position-relative">
                            <Form.Label className="fw-semibold">{t("Código de Cliente / ERP")} *</Form.Label>
                            <div className="input-group input-group-sm">
                                <Form.Control
                                    size="sm"
                                    required
                                    placeholder="Ej: CC-8214..."
                                    value={clientCodeInput}
                                    onChange={e => {
                                        const val = e.target.value;
                                        setClientCodeInput(val);
                                        fetchErpSuggestions(val);
                                    }}
                                    onFocus={() => {
                                        if (suggestions.length > 0) setShowSuggestions(true);
                                    }}
                                />
                                <Button
                                    variant="outline-secondary"
                                    className="px-3"
                                    type="button"
                                    title="Buscar en ERP"
                                    onClick={() => fetchErpSuggestions(clientCodeInput)}
                                >
                                    {isSearchingErp ? (
                                        <Spinner animation="border" size="sm" variant="primary" />
                                    ) : (
                                        <i className="fas fa-search" />
                                    )}
                                </Button>
                            </div>
                            <Form.Text className="text-muted">
                                Escribe o presiona la lupa para buscar clientes en el sistema ERP.
                            </Form.Text>
                        </Col>

                        <Col xs={12} className="position-relative">
                            <Form.Label className="fw-semibold">{t("Nombre del Cliente / Razón Social")} *</Form.Label>
                            <Form.Control
                                size="sm"
                                required
                                placeholder="Ej: CENTRAL HIERRO S.R.L"
                                value={clientNameInput}
                                onChange={e => {
                                    const val = e.target.value;
                                    setClientNameInput(val);
                                    fetchErpSuggestions(val);
                                }}
                                onFocus={() => {
                                    if (suggestions.length > 0) setShowSuggestions(true);
                                }}
                            />

                            {/* ERP Autocomplete Suggestions Dropdown List */}
                            {showSuggestions && suggestions.length > 0 && (
                                <div
                                    className="position-absolute w-100 bg-white border rounded-3 shadow-lg mt-1 overflow-auto z-3"
                                    style={{ maxHeight: "200px" }}
                                >
                                    <div className="bg-light px-3 py-1 text-muted fw-bold extra-small text-uppercase border-bottom">
                                        Clientes ERP Sugeridos ({suggestions.length})
                                    </div>
                                    {suggestions.map((item, idx) => (
                                        <div
                                            key={idx}
                                            className="px-3 py-2 border-bottom hover-bg-light cursor-pointer d-flex align-items-center justify-content-between"
                                            style={{ cursor: "pointer" }}
                                            onMouseDown={(e) => {
                                                e.preventDefault();
                                                setClientCodeInput(item.code || "");
                                                setClientNameInput(item.name || "");
                                                setShowSuggestions(false);
                                            }}
                                        >
                                            <div>
                                                <div className="fw-bold text-primary font-monospace small">{item.code}</div>
                                                <div className="fw-semibold text-dark extra-small">{item.name}</div>
                                            </div>
                                            <Badge bg="primary" bg-opacity-10 text-primary className="small">
                                                Autocompletar
                                            </Badge>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" size="sm" onClick={() => setShowAccountModal(false)}>
                        {t("Cancelar")}
                    </Button>
                    <Button variant="primary" size="sm" type="submit" disabled={isSavingAccount}>
                        {isSavingAccount ? t("Guardando...") : t("Guardar Vinculación")}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    </div>;
};

export default Summary;